/**
 * Dynamic size chart (configured-dynamic-size-chart.liquid).
 * Config per instance: [data-sc-chart-root] + data-sc-chart-json.
 */
(function () {
  'use strict';

  // INT-1785347404: rendered at most ~600-700px wide inside .sc-htm-frame (see
  // custom-options.css), not the 2048px master — capped via a literal query param since this
  // is a static JS string outside the Liquid render pipeline cdn-image-guard.liquid covers.
  const SC_MEASURE_IMAGE_DEFAULT =
    'https://cdn.shopify.com/s/files/1/0537/2585/5916/files/IconicBatmanLogoSweatpants2_2048x_d6545739-ec2a-4db6-8078-fd1c2befb7f1.webp?v=1779270051&width=800';

  function parseSizeChartJSON(raw) {
    if (!raw) return null;
    let obj = raw;
    if (typeof raw === 'string') {
      try {
        obj = JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }
    if (!obj || typeof obj !== 'object') return null;
    if (!Object.prototype.hasOwnProperty.call(obj, '_readme')) return obj;
    const copy = Object.assign({}, obj);
    delete copy._readme;
    return copy;
  }

  function initSizeChartRoot(scRoot, hasPrimaryOnPage) {
    if (!scRoot || scRoot.dataset.scInitialized === 'true') return;
    scRoot.dataset.scInitialized = 'true';

    const isPrimary = scRoot.hasAttribute('data-sc-primary');
    const sizeChartJSON = parseSizeChartJSON(scRoot.dataset.scChartJson);

    const SC_DEFAULTS = {
      modal_title: 'Size Chart',
      tabs: { guide: 'Size Guide', measure: 'How to Measure' },
      guide: {
        heading: "Men's Sweats: Find Your Perfect Fit",
        note: 'Size chart is based on <strong>your body measurements</strong>, not garment measurements.',
        footer_note: 'For the most comfortable fit, choose the size that matches your largest measurement.',
        size_column: 'Size',
        columns: ['Your Waist', 'Your Hips', 'Sweatpants Inseam', 'Sweatshorts Inseam'],
        unit_labels: { in: 'IN', cm: 'CM' },
        finder: {
          label: 'My waist is',
          placeholder: '30',
          unit_in: 'in',
          unit_cm: 'cm'
        }
      },
      measure: {
        image: SC_MEASURE_IMAGE_DEFAULT,
        annotations: [
          {
            position: 'left',
            top: '55%',
            label: 'Hip',
            desc: 'Measure around the fullest part of your hips, tape parallel to the floor.'
          },
          {
            position: 'right',
            top: '38%',
            label: 'Waist',
            desc: 'Measure around your natural waist, just above the navel.'
          },
          {
            position: 'bottom',
            left: '40%',
            label: 'Inseam',
            desc: 'From the inner thigh straight down to the bottom of the leg.'
          }
        ],
        instructions: [
          'Use a tape measure and these guidelines to determine your size.',
          "If you don't have a tape measure, you can use a piece of string or ribbon and then measure it against a ruler."
        ]
      }
    };

    let scUnit = 'in';
    let scMatchSize = null;
    let scChartJson = null;
    let scNormalized = null;
    let scEscapeBound = false;

    function toCm(inches) {
      return Math.round(inches * 2.54 * 2) / 2;
    }

    function escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function extractImageUrl(value) {
      if (!value) return '';
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'object' && value.url) return String(value.url).trim();
      return '';
    }

    function isLegacyMeasureSchema(measure) {
      if (!measure || typeof measure !== 'object') return false;
      if (measure.annotations && measure.annotations.length) return false;
      if (measure.instructions && measure.instructions.length) return false;
      return !!(measure.text || measure.html);
    }

    function resolveMeasureImage(measure) {
      if (!measure || typeof measure !== 'object') return SC_MEASURE_IMAGE_DEFAULT;
      if (isLegacyMeasureSchema(measure)) return SC_MEASURE_IMAGE_DEFAULT;
      const url = extractImageUrl(
        measure.image || measure.image_url || measure.photo || measure.src
      );
      return url || SC_MEASURE_IMAGE_DEFAULT;
    }

    function getScRoot() {
      return scRoot;
    }

    /** Normalize legacy regions.* and new guide.sizes.* into one shape */
    function normalizeChartData(json) {
      if (!json) return null;
      const guide = json.guide || {};
      let rowsIn = [];
      let rowsCm = [];
      let columns = guide.columns && guide.columns.length ? guide.columns.slice() : null;

      if (guide.sizes && (guide.sizes.in || guide.sizes.cm)) {
        rowsIn =
          guide.sizes.in && guide.sizes.in.length ? guide.sizes.in : [];
        rowsCm =
          guide.sizes.cm && guide.sizes.cm.length ? guide.sizes.cm : rowsIn;
        if (!rowsIn.length && rowsCm.length) rowsIn = rowsCm;
      } else if (guide.regions && typeof guide.regions === 'object') {
        const regionKey = guide.default_region || Object.keys(guide.regions)[0];
        const region = guide.regions[regionKey];
        if (region) {
          rowsIn = region.in || [];
          rowsCm = region.cm || region.in || [];
        }
      }

      if (!columns && rowsIn.length) {
        const sample = rowsIn[0];
        if (sample.waist_in || sample.hips_in) {
          columns = SC_DEFAULTS.guide.columns.slice();
        } else {
          columns = Object.keys(sample).filter(function (k) {
            return k !== 'size' && k !== 'waist_range' && k !== 'waist_in' && k !== 'hips_in';
          });
        }
      }
      if (!columns) columns = SC_DEFAULTS.guide.columns.slice();

      const measure = json.measure || {};
      const defaultAnn = SC_DEFAULTS.measure.annotations;
      const defaultInstr = SC_DEFAULTS.measure.instructions;
      const legacyMeasure = isLegacyMeasureSchema(measure);

      return {
        modalTitle: json.modal_title || SC_DEFAULTS.modal_title,
        tabGuide: (json.tabs && json.tabs.guide) || SC_DEFAULTS.tabs.guide,
        tabMeasure: (json.tabs && json.tabs.measure) || SC_DEFAULTS.tabs.measure,
        heading:
          guide.heading ||
          (guide.description && guide.description.heading) ||
          SC_DEFAULTS.guide.heading,
        note: guide.note || SC_DEFAULTS.guide.note,
        footer:
          guide.footer_note || guide.text_below_table || SC_DEFAULTS.guide.footer_note,
        sizeColumn: guide.size_column || SC_DEFAULTS.guide.size_column,
        columns: columns,
        rowsIn: rowsIn,
        rowsCm: rowsCm,
        unitIn: (guide.unit_labels && guide.unit_labels.in) || SC_DEFAULTS.guide.unit_labels.in,
        unitCm: (guide.unit_labels && guide.unit_labels.cm) || SC_DEFAULTS.guide.unit_labels.cm,
        finder: {
          label: (guide.finder && guide.finder.label) || SC_DEFAULTS.guide.finder.label,
          placeholder:
            (guide.finder && guide.finder.placeholder) || SC_DEFAULTS.guide.finder.placeholder,
          unitIn: (guide.finder && guide.finder.unit_in) || SC_DEFAULTS.guide.finder.unit_in,
          unitCm: (guide.finder && guide.finder.unit_cm) || SC_DEFAULTS.guide.finder.unit_cm
        },
        measure: {
          image: resolveMeasureImage(measure),
          annotations:
            measure.annotations && measure.annotations.length
              ? measure.annotations
              : defaultAnn,
          instructions:
            measure.instructions && measure.instructions.length
              ? measure.instructions
              : legacyMeasure
                ? defaultInstr
                : measure.text
                  ? Array.isArray(measure.text)
                    ? measure.text
                    : [String(measure.text)]
                  : defaultInstr
        }
      };
    }

    function getRows() {
      if (!scNormalized) return [];
      const rowsIn = scNormalized.rowsIn || [];
      const rowsCm = scNormalized.rowsCm || [];
      if (scUnit === 'cm') return rowsCm.length ? rowsCm : rowsIn;
      return rowsIn.length ? rowsIn : rowsCm;
    }

    function getFinderRows() {
      return getRows();
    }

    function sizesMatch(a, b) {
      return String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
    }

    /** Waist min/max in the active unit (in or cm) for size-finder matching. */
    function getWaistRangeForCurrentUnit(row) {
      if (!row) return null;
      if (scUnit === 'cm' && row.waist_cm && row.waist_cm.length >= 2) {
        return [Number(row.waist_cm[0]), Number(row.waist_cm[1])];
      }
      if (scUnit === 'in' && row.waist_cm && row.waist_cm.length >= 2) {
        return [Number(row.waist_cm[0]) / 2.54, Number(row.waist_cm[1]) / 2.54];
      }
      if (row.waist_in && row.waist_in.length >= 2) {
        const lo = Number(row.waist_in[0]);
        const hi = Number(row.waist_in[1]);
        if (scUnit === 'cm') return [toCm(lo), toCm(hi)];
        return [lo, hi];
      }
      if (row.waist_range && row.waist_range.length >= 2) {
        const a = Number(row.waist_range[0]);
        const b = Number(row.waist_range[1]);
        if (scUnit === 'cm' && a > 50) return [a / 2.54, b / 2.54];
        return [a, b];
      }
      const waistKey = Object.keys(row).find(function (k) {
        return k !== 'size' && /waist/i.test(k) && k !== 'waist_range' && k !== 'waist_in' && k !== 'waist_cm';
      });
      if (!waistKey) return null;
      const match = String(row[waistKey]).match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
      if (!match) return null;
      let lo = parseFloat(match[1]);
      let hi = parseFloat(match[2]);
      if (scUnit === 'cm' && lo > 50) {
        lo /= 2.54;
        hi /= 2.54;
      }
      return [lo, hi];
    }

    function waistValueInSizeRange(waistValue, min, max) {
      return waistValue >= min - 0.001 && waistValue <= max + 0.001;
    }

    function formatCmValue(n) {
      const num = Number(n);
      if (isNaN(num)) return String(n);
      return num % 1 === 0 ? String(num) : String(num);
    }

    function measureBaseFromLabel(col) {
      return col
        .replace(/^your\s+/, '')
        .replace(/\s*\(.*$/, '')
        .trim()
        .replace(/\s+/g, '_');
    }

    function measureUnitSuffix(base) {
      if (base === 'weight') return scUnit === 'cm' ? ' kg' : ' lb';
      return scUnit === 'cm' ? ' cm' : '"';
    }

    function formatMeasureRange(base, lo, hi) {
      return formatCmValue(lo) + '–' + formatCmValue(hi) + measureUnitSuffix(base);
    }

    function cellValue(row, colKey) {
      const col = colKey.toLowerCase();
      const base = measureBaseFromLabel(col);
      const inVal = row[base + '_in'];
      const cmVal = row[base + '_cm'];

      if (scUnit === 'cm') {
        if (Array.isArray(cmVal) && cmVal.length >= 2) {
          return formatMeasureRange(base, cmVal[0], cmVal[1]);
        }
        if (Array.isArray(inVal) && inVal.length >= 2) {
          if (base === 'weight') return formatMeasureRange(base, inVal[0], inVal[1]);
          return formatMeasureRange(base, toCm(inVal[0]), toCm(inVal[1]));
        }
      } else {
        if (Array.isArray(inVal) && inVal.length >= 2) {
          return formatMeasureRange(base, inVal[0], inVal[1]);
        }
        if (Array.isArray(cmVal) && cmVal.length >= 2) {
          if (base === 'weight') return formatMeasureRange(base, cmVal[0], cmVal[1]);
          return formatMeasureRange(base, cmVal[0] / 2.54, cmVal[1] / 2.54);
        }
      }

      const key = Object.keys(row).find(function (k) {
        return k.toLowerCase() === col || k === colKey;
      });
      if (key && row[key] != null) return String(row[key]);

      if (col.indexOf('pants') !== -1 || col.indexOf('sweatpants') !== -1) {
        return row.pants_in || row.pantsIn || row['Sweatpants Inseam'] || '-';
      }
      if (col.indexOf('short') !== -1) {
        return row.shorts_in || row.shortsIn || row['Sweatshorts Inseam'] || '-';
      }
      return '-';
    }

    function getCurrentSelectedSize() {
      const checked = document.querySelector(
        'variant-pills input[type="radio"][name*="-Size-"]:checked, variant-pills input[type="radio"][name*="-size-"]:checked'
      );
      if (checked && checked.value) return String(checked.value).trim();
      const allChecked = document.querySelector('variant-pills input[type="radio"]:checked');
      if (allChecked && allChecked.value) return String(allChecked.value).trim();
      const trigger = document.querySelector('.custom-select .select-trigger');
      if (trigger && trigger.textContent && trigger.textContent !== 'Select option') {
        return trigger.textContent.trim();
      }
      return null;
    }

    function selectSizeInBuyBox(sizeLabel) {
      const sizeNorm = String(sizeLabel).trim();
      if (!sizeNorm) return false;

      const radios = document.querySelectorAll('variant-pills input[type="radio"]');
      for (const input of radios) {
        if (String(input.value).trim() !== sizeNorm) continue;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        const label = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        if (label) label.click();
        document.querySelectorAll('.selected-value[data-option-index]').forEach(function (span) {
          span.textContent = sizeNorm;
        });
        return true;
      }

      const opt = document.querySelector('.option-value[data-value="' + CSS.escape(sizeNorm) + '"]');
      if (opt) {
        opt.click();
        return true;
      }
      return false;
    }

    function getScModal() {
      const root = getScRoot();
      if (root) return root.querySelector('.sc-modal');
      const primary = document.querySelector('[data-sc-chart-root][data-sc-primary="true"]');
      return primary ? primary.querySelector('.sc-modal') : null;
    }

    function scQuery(id) {
      const modal = getScModal();
      if (!modal) return null;
      return modal.querySelector('[id="' + id + '"]');
    }

    function bindScModalClose() {
      const modal = getScModal();
      if (!modal || modal.dataset.scBound === 'true') return;
      modal.dataset.scBound = 'true';

      const closeBtn = scQuery('scClose');
      if (closeBtn) closeBtn.addEventListener('click', closeScModal);
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeScModal();
      });

      if (!scEscapeBound) {
        scEscapeBound = true;
        document.addEventListener('keydown', function (e) {
          const m = getScModal();
          if (e.key === 'Escape' && m && m.classList.contains('open')) closeScModal();
        });
      }
    }

    function switchTab(tab) {
      const guideBtn = scQuery('scTabGuide');
      const measureBtn = scQuery('scTabMeasure');
      const guideBody = scQuery('scBodyGuide');
      const measureBody = scQuery('scBodyMeasure');
      const isGuide = tab === 'guide';

      if (guideBtn) {
        guideBtn.classList.toggle('active', isGuide);
        guideBtn.setAttribute('aria-selected', isGuide ? 'true' : 'false');
      }
      if (measureBtn) {
        measureBtn.classList.toggle('active', !isGuide);
        measureBtn.setAttribute('aria-selected', !isGuide ? 'true' : 'false');
      }
      if (guideBody) guideBody.hidden = !isGuide;
      if (measureBody) measureBody.hidden = isGuide;
    }

    function applyModalCopy() {
      if (!scNormalized) return;
      const title = scQuery('scModalTitle');
      if (title) title.textContent = scNormalized.modalTitle;
      const tabGuide = scQuery('scTabGuide');
      if (tabGuide) tabGuide.textContent = scNormalized.tabGuide;
      const tabMeasure = scQuery('scTabMeasure');
      if (tabMeasure) tabMeasure.textContent = scNormalized.tabMeasure;
      const heading = scQuery('scGuideHeading');
      if (heading) heading.textContent = scNormalized.heading;
      const note = scQuery('scGuideNote');
      if (note) note.innerHTML = scNormalized.note;
      const footer = scQuery('scFooterNote');
      if (footer) footer.textContent = scNormalized.footer;
      const unitIn = scQuery('scUnitIn');
      if (unitIn) unitIn.textContent = scNormalized.unitIn;
      const unitCm = scQuery('scUnitCm');
      if (unitCm) unitCm.textContent = scNormalized.unitCm;
      const finderLabel = scQuery('scFinderLabel');
      if (finderLabel) finderLabel.textContent = scNormalized.finder.label;
      const waistInput = scQuery('scWaistInput');
      if (waistInput) waistInput.placeholder = scNormalized.finder.placeholder;
      const finderUnit = scQuery('scFinderUnit');
      if (finderUnit) {
        finderUnit.textContent =
          scUnit === 'cm' ? scNormalized.finder.unitCm : scNormalized.finder.unitIn;
      }
    }

    function renderTableHead() {
      const headRow = scQuery('scTableHeadRow');
      if (!headRow) return;
      headRow.innerHTML = '<th>' + escapeHtml(scNormalized.sizeColumn) + '</th>';
      scNormalized.columns.forEach(function (col) {
        const th = document.createElement('th');
        th.textContent = col;
        headRow.appendChild(th);
      });
    }

    function renderScTable() {
      const tbody = scQuery('scTableBody');
      if (!tbody) return;
      tbody.innerHTML = '';
      const rows = getRows();
      if (!rows.length) {
        tbody.innerHTML =
          '<tr><td colspan="' +
          (scNormalized.columns.length + 1) +
          '">No size data available.</td></tr>';
        return;
      }

      const selectedSize = getCurrentSelectedSize();

      rows.forEach(function (row) {
        const sizeLabel = row.size || '-';
        const isMatch = scMatchSize && sizesMatch(scMatchSize, sizeLabel);
        const isSel = selectedSize && sizesMatch(selectedSize, sizeLabel);
        const tr = document.createElement('tr');
        if (isMatch) tr.classList.add('sc-row-match');
        tr.classList.add('sc-size-row');
        tr.setAttribute('data-size', sizeLabel);
        tr.setAttribute('tabindex', '0');
        tr.setAttribute('role', 'button');

        const hasBadge = isSel || isMatch;
        let sizeInner = escapeHtml(sizeLabel);
        if (isSel) sizeInner += '<span class="sc-selected-tag">Selected</span>';
        if (isMatch) sizeInner += '<span class="sc-match-tag">Your Size</span>';

        const sizeTd = document.createElement('td');
        if (hasBadge) {
          sizeTd.innerHTML = '<span class="sc-size-cell">' + sizeInner + '</span>';
        } else {
          sizeTd.textContent = sizeLabel;
        }
        tr.appendChild(sizeTd);

        scNormalized.columns.forEach(function (col) {
          const td = document.createElement('td');
          td.textContent = cellValue(row, col);
          tr.appendChild(td);
        });

        tr.addEventListener('click', function () {
          if (selectSizeInBuyBox(sizeLabel)) closeScModal();
        });
        tr.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            tr.click();
          }
        });

        tbody.appendChild(tr);
      });
    }

    const SC_WAIST_MIN = 0;

    function clampWaistValue(value) {
      return Math.max(SC_WAIST_MIN, value);
    }

    function normalizeWaistForUnit(value) {
      const n = clampWaistValue(value);
      if (scUnit === 'cm') return Math.round(n * 2) / 2;
      return Math.round(n);
    }

    function formatWaistInputValue(value) {
      const n = normalizeWaistForUnit(value);
      return scUnit === 'cm' && n % 1 !== 0 ? String(n) : String(Math.round(n));
    }

    function parseWaistInput(rawValue) {
      const trimmed = String(rawValue).trim();
      if (trimmed === '' || trimmed === '.' || trimmed === '-') return null;
      const n = parseFloat(trimmed);
      if (isNaN(n)) return null;
      return clampWaistValue(n);
    }

    function setWaistInputValue(input, value) {
      if (!input) return;
      input.value = formatWaistInputValue(value);
    }

    function syncWaistInputConstraints() {
      const input = scQuery('scWaistInput');
      if (!input) return;
      input.step = scUnit === 'cm' ? '0.5' : '1';
    }

    /**
     * Your Size when waist is inside a row band (CM uses waist_cm bands from JSON).
     * IN: exact band match first; else batman-style fill for gaps between inch ranges only.
     * Below all mins or above all maxes → no match.
     */
    function findMatchSizeByWaist(waistValue, rows) {
      if (!rows || !rows.length || isNaN(waistValue)) return null;

      const sized = [];
      for (let i = 0; i < rows.length; i++) {
        const range = getWaistRangeForCurrentUnit(rows[i]);
        if (!range) continue;
        sized.push({ size: rows[i].size, min: range[0], max: range[1] });
      }
      if (!sized.length) return null;

      for (let j = 0; j < sized.length; j++) {
        if (waistValueInSizeRange(waistValue, sized[j].min, sized[j].max)) {
          return sized[j].size;
        }
      }

      if (waistValue < sized[0].min || waistValue > sized[sized.length - 1].max) {
        return null;
      }

      if (scUnit === 'in') {
        let match = sized[0].size;
        for (let k = 0; k < sized.length; k++) {
          if (waistValue >= sized[k].min) match = sized[k].size;
        }
        return match;
      }

      return null;
    }

    function setFinderError(msg) {
      const errorEl = scQuery('scFinderError');
      const inputEl = scQuery('scWaistInput');
      if (msg) {
        if (errorEl) { errorEl.textContent = msg; errorEl.hidden = false; }
        if (inputEl) inputEl.classList.add('sc-finder-input--error');
      } else {
        if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
        if (inputEl) inputEl.classList.remove('sc-finder-input--error');
      }
    }

    function setFinderWarning(msg) {
      setFinderError(msg || null);
    }

    function getWaistBounds(rows) {
      let lo = null, hi = null;
      for (let i = 0; i < rows.length; i++) {
        const range = getWaistRangeForCurrentUnit(rows[i]);
        if (!range) continue;
        if (lo === null || range[0] < lo) lo = range[0];
        if (hi === null || range[1] > hi) hi = range[1];
      }
      return { lo: lo, hi: hi };
    }

    function onWaistFinderInput(rawValue) {
      const trimmed = String(rawValue).trim();
      if (trimmed === '' || trimmed === '.' || trimmed === '-') {
        scMatchSize = null;
        setFinderWarning(null);
        renderScTable();
        return;
      }
      const parsed = parseFloat(trimmed);
      if (isNaN(parsed)) {
        scMatchSize = null;
        setFinderWarning(null);
        renderScTable();
        return;
      }
      const raw = normalizeWaistForUnit(parsed);

      const rows = getFinderRows();
      if (!rows.length) {
        scMatchSize = null;
        setFinderWarning(null);
        renderScTable();
        return;
      }

      const bounds = getWaistBounds(rows);
      const unit = scUnit === 'cm' ? ' cm' : '"';

      if (bounds.lo !== null && raw < bounds.lo) {
        setFinderWarning('Below minimum (' + bounds.lo + unit + '). Showing smallest size.');
        scMatchSize = rows[0].size;
      } else if (bounds.hi !== null && raw > bounds.hi) {
        setFinderWarning('Above maximum (' + bounds.hi + unit + '). Showing largest size.');
        scMatchSize = rows[rows.length - 1].size;
      } else {
        setFinderWarning(null);
        scMatchSize = findMatchSizeByWaist(raw, rows);
      }

      renderScTable();
      const modal = getScModal();
      const matchRow = modal && modal.querySelector('.sc-table tbody tr.sc-row-match');
      if (matchRow) matchRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    function applyMeasureInstructions() {
      const explain = scQuery('scMeasureExplain');
      if (!explain || !scNormalized) return;
      explain.innerHTML = '';
      scNormalized.measure.instructions.forEach(function (line) {
        const p = document.createElement('p');
        p.textContent = typeof line === 'string' ? line : '';
        explain.appendChild(p);
      });
    }

    function renderMeasureTab() {
      const host = scQuery('scMeasurePhotoHost');
      if (!host || !scNormalized) return;
      host.innerHTML = '';
      const m = scNormalized.measure;
      const imageUrl = m.image || SC_MEASURE_IMAGE_DEFAULT;

      if (imageUrl) {
        const frame = document.createElement('div');
        frame.className = 'sc-htm-frame';
        const img = document.createElement('img');
        img.className = 'sc-htm-photo';
        img.src = imageUrl;
        img.alt = 'Measurement reference';
        img.loading = 'lazy';
        frame.appendChild(img);

        const annotations = m.annotations || [];

        annotations.forEach(function (ann) {
          const pos = ann.position || 'left';
          const annEl = document.createElement('div');
          annEl.className =
            'sc-ann ' + (pos === 'right' ? 'sc-ann-r' : pos === 'bottom' ? 'sc-ann-b' : 'sc-ann-l');
          if (ann.top) annEl.style.top = ann.top;
          if (ann.left) annEl.style.left = ann.left;

          function annBox(className) {
            const box = document.createElement('div');
            box.className = className;
            const pill = document.createElement('span');
            pill.className = 'sc-ann-pill';
            pill.textContent = ann.label || '';
            box.appendChild(pill);
            if (ann.desc) {
              const p = document.createElement('p');
              p.className = 'sc-ann-desc';
              p.textContent = ann.desc;
              box.appendChild(p);
            }
            return box;
          }

          if (pos === 'left') {
            annEl.appendChild(annBox('sc-ann-box sc-ann-box-l'));
            const line = document.createElement('div');
            line.className = 'sc-ann-line';
            annEl.appendChild(line);
            const dot = document.createElement('div');
            dot.className = 'sc-ann-dot';
            annEl.appendChild(dot);
          } else if (pos === 'right') {
            const dot = document.createElement('div');
            dot.className = 'sc-ann-dot';
            annEl.appendChild(dot);
            const line = document.createElement('div');
            line.className = 'sc-ann-line';
            annEl.appendChild(line);
            annEl.appendChild(annBox('sc-ann-box'));
          } else {
            const dot = document.createElement('div');
            dot.className = 'sc-ann-dot';
            annEl.appendChild(dot);
            const vline = document.createElement('div');
            vline.className = 'sc-ann-vline';
            annEl.appendChild(vline);
            annEl.appendChild(annBox('sc-ann-box sc-ann-box-b'));
          }
          frame.appendChild(annEl);
        });

        host.appendChild(frame);
      }

      applyMeasureInstructions();
    }

    function bindControls() {
      const tabGuide = scQuery('scTabGuide');
      const tabMeasure = scQuery('scTabMeasure');
      const unitInBtn = scQuery('scUnitIn');
      const unitCmBtn = scQuery('scUnitCm');
      const waistInput = scQuery('scWaistInput');

      if (tabGuide) tabGuide.addEventListener('click', function () { switchTab('guide'); });
      if (tabMeasure) tabMeasure.addEventListener('click', function () { switchTab('measure'); });

      if (unitInBtn) {
        unitInBtn.addEventListener('click', function () {
          scUnit = 'in';
          unitInBtn.classList.add('active');
          if (unitCmBtn) unitCmBtn.classList.remove('active');
          const finderUnit = scQuery('scFinderUnit');
          if (finderUnit) finderUnit.textContent = scNormalized.finder.unitIn;
          const input = scQuery('scWaistInput');
          const v = input && parseFloat(input.value);
          if (input && !isNaN(v)) setWaistInputValue(input, v / 2.54);
          syncWaistInputConstraints();
          onWaistFinderInput(input.value);
        });
      }

      if (unitCmBtn) {
        unitCmBtn.addEventListener('click', function () {
          scUnit = 'cm';
          unitCmBtn.classList.add('active');
          if (unitInBtn) unitInBtn.classList.remove('active');
          const finderUnit = scQuery('scFinderUnit');
          if (finderUnit) finderUnit.textContent = scNormalized.finder.unitCm;
          const input = scQuery('scWaistInput');
          const v = input && parseFloat(input.value);
          if (input && !isNaN(v)) setWaistInputValue(input, v * 2.54);
          syncWaistInputConstraints();
          onWaistFinderInput(input.value);
        });
      }

      if (waistInput) {
        const spinWrap = waistInput.closest('.sc-finder-input-wrap');
        const spinUp = spinWrap && spinWrap.querySelector('.sc-finder-spin-up');
        const spinDown = spinWrap && spinWrap.querySelector('.sc-finder-spin-down');

        function stepWaist(delta) {
          const current = parseWaistInput(waistInput.value);
          const base = current === null ? SC_WAIST_MIN : current;
          const step = scUnit === 'cm' ? 0.5 : 1;
          setWaistInputValue(waistInput, base + delta * step);
          onWaistFinderInput(waistInput.value);
        }

        if (spinUp) {
          spinUp.addEventListener('click', function (e) {
            e.preventDefault();
            stepWaist(1);
          });
        }
        if (spinDown) {
          spinDown.addEventListener('click', function (e) {
            e.preventDefault();
            stepWaist(-1);
          });
        }

        waistInput.addEventListener('input', function (e) {
          onWaistFinderInput(e.target.value);
        });
        waistInput.addEventListener('change', function (e) {
          onWaistFinderInput(e.target.value);
        });
        waistInput.addEventListener('blur', function (e) {
          const v = parseWaistInput(e.target.value);
          if (v === null) return;
          e.target.value = formatWaistInputValue(v);
          onWaistFinderInput(e.target.value);
        });
      }

      syncWaistInputConstraints();
    }

    function openSizeChart() {
      if (!sizeChartJSON) {
        if (typeof window.handleSizeChartPopup === 'function') window.handleSizeChartPopup();
        return;
      }
      scChartJson = sizeChartJSON;
      scNormalized = normalizeChartData(sizeChartJSON);
      if (!scNormalized) return;

      const modal = getScModal();
      if (!modal) return;
      bindScModalClose();
      applyModalCopy();
      renderTableHead();
      renderScTable();
      renderMeasureTab();
      switchTab('guide');
      syncWaistInputConstraints();

      modal.hidden = false;
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    function closeScModal() {
      const modal = getScModal();
      if (modal) {
        modal.classList.remove('open');
        modal.hidden = true;
      }
      document.body.style.overflow = '';
      scMatchSize = null;
      setFinderWarning(null);
      const waistInput = scQuery('scWaistInput');
      if (waistInput) waistInput.value = '';
    }

    function publishSizeChartGlobals() {
      if (!scRoot) return;
      if (!isPrimary && hasPrimaryOnPage) return;
      window.openSizeChart = openSizeChart;
      window.closeScModal = closeScModal;
      window.handleSizeChartPopupDynamic = openSizeChart;
      window.closePopupDynamic = closeScModal;
      window.openNativePajamaSizeChart = function (event) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
        openSizeChart();
        return false;
      };
    }

    bindControls();
    publishSizeChartGlobals();
    if (isPrimary) {
      document.addEventListener('DOMContentLoaded', publishSizeChartGlobals);
    }

    document.addEventListener('change', function (e) {
      if (!getScModal() || !getScModal().classList.contains('open')) return;
      if (e.target && e.target.matches && e.target.matches('variant-pills input[type="radio"]')) {
        renderScTable();
      }
    });
  }

  function boot() {
    const roots = document.querySelectorAll('[data-sc-chart-root]');
    if (!roots.length) return;
    const hasPrimaryOnPage = !!document.querySelector('[data-sc-chart-root][data-sc-primary="true"]');
    const firstRoot = roots[0];
    roots.forEach(function (scRoot) {
      const isPrimary = scRoot.hasAttribute('data-sc-primary');
      const shouldInit =
        isPrimary || (!hasPrimaryOnPage && scRoot === firstRoot);
      if (shouldInit) initSizeChartRoot(scRoot, hasPrimaryOnPage);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('surge:combined-listing-shell-replaced', boot);
})();
